import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs';
import { DialogResult, DialogService, DspxCardSetBaseFormComponent, FormNavigationService, MessagePlaceholders, SpinnerService } from 'dspx-core';
import { API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { APP_DATA_KEYS, FILTER_ACTIONS, FILTER_QUESTION_CODES, FORM_ACTIONS, FORM_CUSTOM_ACTIONS, minWaitForDialogToCloseMs, NAV_TAB_NUMBERS, noFilterOptionCode, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, SECTION_CODES, SERVER_ERROR_POP_UP_CODES } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES } from '../../constants/templates.constants';
import { AppDataService } from '../../services/app-data.service';
import { QuestionPath, ResponseParserService } from '../../services/response-parser.service';
import { ErrorService } from '../../services/error.service';
import { ExcelExportService } from '../../services/excel-export.service';
import { BaseCardSetListComponent } from "../base-card-set-list-component/base-card-set-list.component";
import { CoiFormGridFilterRequest } from '../../interfaces/coi-form-grid-filter-request.interface';

@Component({
  selector: 'app-coi-form-list',
  templateUrl: './coi-form-list.component.html',
  styleUrl: './coi-form-list.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class CoiFormListComponent extends BaseCardSetListComponent implements OnInit, OnDestroy {

  activeDialog: string;

  @ViewChild(DspxCardSetBaseFormComponent)
  private set _baseForm(value: DspxCardSetBaseFormComponent) {
    this.baseForm = value
  }

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected spinnerService: SpinnerService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected appDataService: AppDataService,
    private errorService: ErrorService,
    private excelExportService: ExcelExportService,
    private formNavigationService: FormNavigationService) {

    super(
      router,
      spinnerService,
      dialogService,
      responseParserService,
      appDataService,
      TEMPLATES.coiFormList,
      () => API_ENDPOINTS.dashboard.getCoiForms,
      true);
  }

  ngOnInit(): void {
    this.route.url.subscribe((url) => {
      if (url.length === 0) {
        // In the case where this component is being loaded as part of the default (empty) route, we want to force it
        // to the COI Form List route so that the menu will highlight the selected option.
        this.router.navigate(['/', ROUTE_CONSTANTS.coiForm.list]);
      }
    });

    this.formNavigationService.onNavigation().subscribe(event => {
      setTimeout(() => {
        this.overrideHtmlTitleSetByDspxFramework();
      })
    });
    
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
  }

  formLoad(event: any): void {
    this.loadGridDataOnFormLoad(this.getDefaultFilterRequest());
  }

  filterData(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.coiFormListForm.coiFormsFilter: {
        switch (event.action) {
          case FILTER_ACTIONS.common.applyFilters: {
            this.loadGridData(this.getFilterRequest());
            break;
          }
          case FILTER_ACTIONS.common.clearFilters: {
            this.loadGridData(this.getDefaultFilterRequest());
            break;
          }
        }
        break;
      }
    }
  }

  dialogButtonClicked(event: any): void {

    const activeDialog = this.activeDialog;
    delete this.activeDialog;

    switch (activeDialog) {
      case POP_UP_CODES.coiFormList.reopenWithComment: {
        this.reopenWithComment(event);
        break;
      }
      case POP_UP_CODES.coiFormList.requestFromExternalUser: {
        this.requestFromExternalUser(event);
        break;
      }
    }
  }

  actionButtonClicked(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.coiFormListForm.coiFormsPlaceholder: {
        switch (event.event.type) {
          case FORM_ACTIONS.coiFormListForm.view: {
            const routePrefix = event.event.data.isApproverFormAccessible
              ? ROUTE_CONSTANTS.approverCoiForm.viewPrefix
              : ROUTE_CONSTANTS.coiForm.viewPrefix;
            this.router.navigate(['/', routePrefix, event.event.data.id]);
            break;
          }
          case FORM_ACTIONS.coiFormListForm.edit:
          case FORM_ACTIONS.coiFormListForm.colLink: {
            const routePrefix = event.event.data.isApproverFormAccessible
              ? ROUTE_CONSTANTS.approverCoiForm.prefix
              : ROUTE_CONSTANTS.coiForm.prefix;
            this.router.navigate(['/', routePrefix, event.event.data.id]);
            break;
          }
          case FORM_ACTIONS.coiFormListForm.reopenWithComment: {
            this.openDialogForReopenWithComment(event.event.data);
            break;
          }
          case FORM_ACTIONS.coiFormListForm.reopen: {
            this.reopen(event.event.data);
            break;
          }
          case FORM_ACTIONS.coiFormListForm.delete: {
            this.deleteCoiForm(event.event.data);
            break;
          }
          case FORM_ACTIONS.coiFormListForm.custom: {
            switch (event.event.data.buttonType) {
              case FORM_CUSTOM_ACTIONS.coiFormListForm.customAdd: {
                this.router.navigate(['/', ROUTE_CONSTANTS.coiForm.prefix]);
                break;
              }
              case FORM_CUSTOM_ACTIONS.coiFormListForm.bulkExport: {
                this.excelExportService.exportCoiFormsToXlsx({});
                break;
              }
              case FORM_CUSTOM_ACTIONS.coiFormListForm.requestFromExternalUser: {
                this.openDialogForRequestFromExternalUser();
                break;
              }
            }
            break;
          }
        }
        break;
      }
    }
  }

  private getDefaultFilterRequest(): CoiFormGridFilterRequest {
    return {
      typeId: noFilterOptionCode,
      hasCoi: noFilterOptionCode,
      statusId: noFilterOptionCode
    };
  }

  private getFilterRequest(): CoiFormGridFilterRequest {

    const filterQuestionPath: QuestionPath = [
      NAV_TAB_NUMBERS.coiFormListForm.coiForms,
      PAGE_NUMBERS.coiFormListForm.coiForms,
      SECTION_CODES.coiFormListForm.resultSection,
      QUESTION_CODES.coiFormListForm.coiFormsFilter];

    const filterQuestionModel = this.getQuestionValue(this.model, ...filterQuestionPath);

    const filterRequest: CoiFormGridFilterRequest = {
      coiId: filterQuestionModel?.[FILTER_QUESTION_CODES.coiFormListForm.coiId],
      submissionDateFrom: filterQuestionModel?.[FILTER_QUESTION_CODES.coiFormListForm.submissionDateFrom],
      submissionDateTo: filterQuestionModel?.[FILTER_QUESTION_CODES.coiFormListForm.submissionDateTo],
      typeId: filterQuestionModel?.[FILTER_QUESTION_CODES.coiFormListForm.typeId],
      declarerId: filterQuestionModel?.[FILTER_QUESTION_CODES.coiFormListForm.declarerId],
      approverId: filterQuestionModel?.[FILTER_QUESTION_CODES.coiFormListForm.approverId],
      hasCoi: filterQuestionModel?.[FILTER_QUESTION_CODES.coiFormListForm.hasCoi],
      statusId: filterQuestionModel?.[FILTER_QUESTION_CODES.coiFormListForm.statusId]
    };

    return filterRequest;
  }

  private openDialogForReopenWithComment(coiForm: any): void {
    this.activeDialog = POP_UP_CODES.coiFormList.reopenWithComment;
    this.dialogService.showDialog(this.activeDialog, { coiId: coiForm.coiId, id: coiForm.id });
  }

  private reopenWithComment(model: any): void {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService.post(API_ENDPOINTS.coiForm.reopenWithComment, { id: model.id, approverReopenComment: model.approverReopenComment })
        .pipe(take(1), this.errorService.errorHandler())
        .subscribe(data => {
          this.spinnerService.hideSpinner();

          const isSuccess = !SERVER_ERROR_POP_UP_CODES.reopenWithCommentCoiForm.includes(data.message);
          this.activeDialog = POP_UP_CODES.coiFormList.reopenSuccessful;

          const messagePlaceholders: MessagePlaceholders = { coiId: model.coiId };
          this.dialogService.showDialog(this.activeDialog, null, messagePlaceholders);
          if (isSuccess) {
            this.loadGridData(this.getFilterRequest());
          }
        })
    );
  }

  private reopen(coiForm: any): void {
    const messagePlaceholders: MessagePlaceholders = { coiId: coiForm.coiId };
    this.registerSubscription(
      this.dialogService
        .showConfirm(POP_UP_CODES.coiFormList.reopenConfirmation, messagePlaceholders)
        .pipe(take(1))
        .subscribe(data => {
          switch (data as DialogResult) {
            case DialogResult.ACCEPTED: {
              this.spinnerService.showSpinner();
              this.registerSubscription(
                this.appDataService.post(`${API_ENDPOINTS.coiForm.reopen}/${coiForm.id}`, {})
                  .pipe(take(1), this.errorService.errorHandler())
                  .subscribe(data => {
                    this.spinnerService.hideSpinner();

                    const isSuccess = !SERVER_ERROR_POP_UP_CODES.reopenCoiForm.includes(data.message);
                    this.activeDialog = POP_UP_CODES.coiFormList.reopenSuccessful;

                    this.dialogService.showDialog(this.activeDialog, null, messagePlaceholders);

                    if (isSuccess) {
                      this.loadGridData(this.getFilterRequest());
                    }
                  })
              );
              break;
            }
            case DialogResult.REJECTED: {
              break;
            }
          }
        })
    );
  }

  private deleteCoiForm(coiForm: any): void {
    this.registerSubscription(
      this.dialogService
        .showConfirm(POP_UP_CODES.coiFormList.deleteConfirmation, { coiId: coiForm.coiId })
        .pipe(take(1))
        .subscribe(data => {
          switch (data as DialogResult) {
            case DialogResult.ACCEPTED: {
              this.spinnerService.showSpinner();
              this.registerSubscription(
                this.appDataService.delete(`${API_ENDPOINTS.coiForm.delete}/${coiForm.id}`)
                  .pipe(take(1), this.errorService.errorHandler())
                  .subscribe(data => {
                    this.spinnerService.hideSpinner();

                    const isSuccess = !SERVER_ERROR_POP_UP_CODES.deleteCoiForm.includes(data.message);
                    this.activeDialog = isSuccess ? POP_UP_CODES.coiFormList.deleteSuccessful : data.message;

                    const messagePlaceholders: MessagePlaceholders = { coiId: coiForm.coiId };
                    this.dialogService.showDialog(this.activeDialog, null, messagePlaceholders);

                    if (isSuccess) {
                      this.loadGridData(this.getFilterRequest());
                    }
                  })
              );
              break;
            }
            case DialogResult.REJECTED: {
              break;
            }
          }
        })
    );
  }

  private openDialogForRequestFromExternalUser(model?: any): void {
    this.activeDialog = POP_UP_CODES.coiFormList.requestFromExternalUser;
    this.dialogService.showDialog(this.activeDialog, model);
  }

  private requestFromExternalUser(model: any): void {
    this.registerSubscription(
      this.appDataService.post(API_ENDPOINTS.coiForm.requestFromExternalUser, model)
        .pipe(take(1), this.errorService.errorHandler())
        .subscribe(data => {
          this.spinnerService.hideSpinner();

          const isSuccess = !SERVER_ERROR_POP_UP_CODES.requestCoiFormFromExternalUser.includes(data.message);
          this.activeDialog = isSuccess ? POP_UP_CODES.coiFormList.requestFromExternalUserSuccessful : data.message;

          // To be able to open a new popup we need to wait until the existing popup is being closed, hence the setTimeout
          setTimeout(
            () => {
              if (isSuccess) {
                this.dialogService.showDialog(this.activeDialog);
                this.loadGridData(this.getFilterRequest());
              }
              else {
                const messagePlaceholders: MessagePlaceholders = { applicationUrl: this.appDataService.getAppData(APP_DATA_KEYS.applicationUrl) };
                this.registerSubscription(
                  this.dialogService
                    .showConfirm(this.activeDialog, messagePlaceholders)
                    .pipe(take(1))
                    .subscribe(data => {
                      setTimeout(
                        () => {
                          this.openDialogForRequestFromExternalUser(model);
                        },
                        minWaitForDialogToCloseMs);
                    }));
              }
            },
            minWaitForDialogToCloseMs);
        })
    );
  }
}
